"use client";

import { Button, Input, Label, cn, confirm, toast } from "@devhop/ui";
import {
  Minus,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  ScanLine,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import {
  shopCatalogApi,
  type WarehouseLayoutNodeRow,
  type WarehouseLayoutResult,
} from "../api/client";
import {
  locationDensityFill,
  maxLocationQty,
} from "../lib/warehouse-layout-density";
import {
  useGenerateWarehouseLayoutMutation,
  useReplaceWarehouseLayoutNodesMutation,
  useSetWarehouseLayoutBackgroundMutation,
  useSkuLocationBalancesQuery,
} from "../api/queries";

type Props = {
  shopId: string;
  branchId: string;
  data: WarehouseLayoutResult;
  canEdit: boolean;
  /** Deep-link: focus / highlight this location on the map */
  focusLocationId?: string | null;
  onLocationClick?: (node: WarehouseLayoutNodeRow) => void;
};

const MIN_SCALE = 0.35;
const MAX_SCALE = 2.5;

function nodeKey(node: WarehouseLayoutNodeRow, idx: number): string {
  return (
    node.id ??
    `${node.kind}:${node.refLocationId ?? node.refZoneId ?? idx}`
  );
}

function snap(value: number, grid: number): number {
  return Math.round(value / grid) * grid;
}

function strokeForNode(
  node: WarehouseLayoutNodeRow,
  selected: boolean,
  highlighted: boolean,
  dimmed: boolean,
): string {
  if (highlighted) return "var(--foreground)";
  if (selected) return "var(--foreground)";
  if (dimmed) return "var(--border)";
  if (node.kind === "zone") return "var(--border)";
  if (node.occupancy?.status === "default_receive" || node.isDefaultReceive) {
    return "var(--primary)";
  }
  if ((node.occupancy?.totalQty ?? 0) > 0) return "var(--primary)";
  return "var(--border)";
}

export function WarehouseLayoutEditor({
  shopId,
  branchId,
  data,
  canEdit,
  focusLocationId,
  onLocationClick,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 24, y: 24 });
  const [nodes, setNodes] = useState(data.nodes);
  const [opacity, setOpacity] = useState(data.layout.backgroundOpacity);
  const [bgKey, setBgKey] = useState(data.layout.backgroundImageUrl);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [skuQuery, setSkuQuery] = useState("");
  const [searchSkuId, setSearchSkuId] = useState<string | undefined>();
  const [searchSkuLabel, setSearchSkuLabel] = useState<string | null>(null);
  const [skuSearchPending, setSkuSearchPending] = useState(false);

  const saveMut = useReplaceWarehouseLayoutNodesMutation(shopId);
  const genMut = useGenerateWarehouseLayoutMutation(shopId);
  const bgMut = useSetWarehouseLayoutBackgroundMutation(shopId);
  const skuBalancesQ = useSkuLocationBalancesQuery(
    shopId,
    branchId,
    searchSkuId,
  );

  const grid = data.layout.gridSize || 20;
  const maxQty = useMemo(() => maxLocationQty(nodes), [nodes]);

  const highlightLocationIds = useMemo(() => {
    const set = new Set<string>();
    for (const loc of skuBalancesQ.data?.locations ?? []) {
      if (loc.qtyOnHand > 0) set.add(loc.locationId);
    }
    return set;
  }, [skuBalancesQ.data]);

  const hasSkuHighlight = Boolean(searchSkuId) && !skuBalancesQ.isLoading;

  useEffect(() => {
    setNodes(data.nodes);
    setOpacity(data.layout.backgroundOpacity);
    setBgKey(data.layout.backgroundImageUrl);
    setDirty(false);
    setSelectedKey(null);
  }, [data]);

  const sortedNodes = useMemo(
    () =>
      nodes
        .map((n, idx) => ({ n, idx, key: nodeKey(n, idx) }))
        .sort((a, b) => a.n.zIndex - b.n.zIndex),
    [nodes],
  );

  const centerOnLocation = useCallback(
    (locationId: string) => {
      const el = viewportRef.current;
      const found = nodes.find(
        (n) => n.kind === "location" && n.refLocationId === locationId,
      );
      if (!found || !el) return;
      const idx = nodes.indexOf(found);
      setSelectedKey(nodeKey(found, idx));
      setPan({
        x: el.clientWidth / 2 - (found.x + found.width / 2) * scale,
        y: el.clientHeight / 2 - (found.y + found.height / 2) * scale,
      });
    },
    [nodes, scale],
  );

  useEffect(() => {
    if (!focusLocationId || nodes.length === 0) return;
    centerOnLocation(focusLocationId);
    // Center once when deep-link target or node set arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-pan on scale/pan changes
  }, [focusLocationId, nodes.length]);

  useEffect(() => {
    if (!searchSkuId || skuBalancesQ.isLoading) return;
    const first = [...highlightLocationIds][0];
    if (first) centerOnLocation(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pan once per SKU result
  }, [searchSkuId, skuBalancesQ.isLoading, skuBalancesQ.dataUpdatedAt]);

  async function runSkuSearch() {
    const q = skuQuery.trim();
    if (!q) return;
    setSkuSearchPending(true);
    try {
      let items =
        (
          await shopCatalogApi.listSellable(shopId, {
            limit: 10,
            offset: 0,
            q: undefined,
            barcode: q,
          })
        ).data ?? [];
      if (items.length === 0) {
        items =
          (
            await shopCatalogApi.listSellable(shopId, {
              limit: 10,
              offset: 0,
              q,
              barcode: undefined,
            })
          ).data ?? [];
      }
      const hit = items[0];
      if (!hit) {
        setSearchSkuId(undefined);
        setSearchSkuLabel(null);
        toast.error("ບໍ່ພົບ SKU");
        return;
      }
      setSearchSkuId(hit.id);
      setSearchSkuLabel(`${hit.productName} · ${hit.shopSku}`);
    } catch {
      toast.error("ຄົ້ນຫາ SKU ລົ້ມເຫຼວ");
    } finally {
      setSkuSearchPending(false);
    }
  }

  function clearSkuSearch() {
    setSkuQuery("");
    setSearchSkuId(undefined);
    setSearchSkuLabel(null);
  }

  const fitToView = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const pad = 48;
    const availW = Math.max(el.clientWidth - pad, 120);
    const availH = Math.max(el.clientHeight - pad, 120);
    const next = Math.min(
      availW / data.layout.canvasWidth,
      availH / data.layout.canvasHeight,
      1,
    );
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
    setScale(clamped);
    setPan({
      x: (el.clientWidth - data.layout.canvasWidth * clamped) / 2,
      y: (el.clientHeight - data.layout.canvasHeight * clamped) / 2,
    });
  }, [data.layout.canvasWidth, data.layout.canvasHeight]);

  useEffect(() => {
    fitToView();
  }, [fitToView]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const panDrag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const nodeDrag = useRef<{
    key: string;
    mode: "move" | "resize";
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null>(null);

  const bgSrc = bgKey ? resolveImageSrc(bgKey) : null;

  function updateNode(
    key: string,
    patch: Partial<Pick<WarehouseLayoutNodeRow, "x" | "y" | "width" | "height">>,
  ) {
    setNodes((prev) =>
      prev.map((n, idx) =>
        nodeKey(n, idx) === key ? { ...n, ...patch } : n,
      ),
    );
    setDirty(true);
  }

  function removeSelected() {
    if (!selectedKey) return;
    setNodes((prev) =>
      prev.filter((n, idx) => nodeKey(n, idx) !== selectedKey),
    );
    setSelectedKey(null);
    setDirty(true);
  }

  async function handleSave() {
    await saveMut.mutateAsync({
      branchId,
      layoutId: data.layout.id ?? undefined,
      nodes: nodes.map((n) => ({
        kind: n.kind,
        refZoneId: n.refZoneId,
        refLocationId: n.refLocationId,
        x: n.x,
        y: n.y,
        width: n.width,
        height: n.height,
        rotation: n.rotation,
        labelOverride: n.labelOverride,
        color: n.color,
        zIndex: n.zIndex,
      })),
    });
    setDirty(false);
  }

  async function handleGenerate() {
    if (dirty) {
      const ok = await confirm({
        title: "ສ້າງຜັງຈາກລາຍການໃໝ່?",
        description:
          "ການປ່ຽນແປງທີ່ຍັງບໍ່ບັນທຶກຈະຖືກຖິ້ມ ແລະສ້າງຜັງໃໝ່ຈາກລາຍການ.",
        actionText: "ສ້າງຜັງໃໝ່",
        ActionProps: { variant: "destructive" },
      });
      if (!ok) return;
    }
    await genMut.mutateAsync({
      branchId,
      layoutId: data.layout.id ?? undefined,
      overwrite: true,
    });
    setEditMode(false);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          {data.layout.name}
          <span className="ml-2 font-mono text-xs">
            ຊັ້ນ {data.layout.floorIndex + 1}
            {" · "}
            {data.source === "saved" ? "ບັນທຶກແລ້ວ" : "ອັດຕະໂນມັດ"}
            {dirty ? " · ຍັງບໍ່ບັນທຶກ" : ""}
          </span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setScale((s) =>
                Math.max(MIN_SCALE, Math.min(MAX_SCALE, s - 0.15)),
              )
            }
          >
            <Minus className="size-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setScale((s) =>
                Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + 0.15)),
              )
            }
          >
            <Plus className="size-4" />
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={fitToView}>
            ໃຫ້ເຕັມ
          </Button>
          {canEdit ? (
            <Button
              type="button"
              size="sm"
              variant={editMode ? "default" : "outline"}
              className="gap-1.5"
              onClick={() => {
                setEditMode((v) => !v);
                setSelectedKey(null);
              }}
            >
              <Pencil className="size-4" />
              {editMode ? "ກຳລັງແກ້ໄຂ" : "ແກ້ໄຂ"}
            </Button>
          ) : null}
        </div>
      </div>

      {!editMode ? (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <div className="min-w-[200px] flex-1 space-y-1">
            <Label className="text-xs">ຄົ້ນຫາ SKU ໃນແຜນຜັງ</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={skuQuery}
                className="pl-8"
                placeholder="ບາໂຄດ ຫຼື SKU / ຊື່"
                onChange={(e) => setSkuQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void runSkuSearch();
                  }
                }}
              />
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!skuQuery.trim() || skuSearchPending}
            onClick={() => void runSkuSearch()}
          >
            ຄົ້ນຫາ
          </Button>
          {searchSkuId ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1"
              onClick={clearSkuSearch}
            >
              <X className="size-3.5" />
              ລ້າງ
            </Button>
          ) : null}
          {searchSkuLabel ? (
            <p className="w-full text-muted-foreground text-xs">
              {skuBalancesQ.isLoading
                ? `ກຳລັງໂຫຼດຕຳແໜ່ງຂອງ ${searchSkuLabel}…`
                : highlightLocationIds.size === 0
                  ? `${searchSkuLabel} — ບໍ່ຢູ່ໃນຊ່ອງໃດໆບົນຜັງ`
                  : `${searchSkuLabel} — ${highlightLocationIds.size} ຊ່ອງ`}
            </p>
          ) : null}
          <p className="w-full text-muted-foreground text-xs">
            ຄວາມໜາແໜ່ນ: ສີເຂັ້ມ = ຈຳນວນໃນຊ່ອງຫຼາຍ
            {maxQty > 0 ? ` (ສູງສຸດ ${maxQty})` : ""}
          </p>
        </div>
      ) : null}

      {editMode && canEdit ? (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/20 p-3">
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            disabled={!dirty || saveMut.isPending}
            onClick={() => void handleSave()}
          >
            <Save className="size-4" />
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={!selectedKey}
            onClick={removeSelected}
          >
            <Trash2 className="size-4" />
            ຖອດອອກຈາກຜັງ
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={genMut.isPending}
            onClick={() => void handleGenerate()}
          >
            <RotateCcw className="size-4" />
            ສ້າງຜັງຈາກລາຍການ
          </Button>
          <div className="min-w-[140px] space-y-1">
            <Label className="text-xs">ຄວາມໂປ່ງໃສພື້ນຫຼັງ ({opacity}%)</Label>
            <input
              type="range"
              min={0}
              max={100}
              value={opacity}
              className="w-full"
              onChange={(e) => {
                const next = Number(e.target.value);
                setOpacity(next);
              }}
              onMouseUp={() => {
                void bgMut.mutateAsync({
                  branchId,
                  layoutId: data.layout.id ?? undefined,
                  backgroundImageUrl: bgKey,
                  backgroundOpacity: opacity,
                });
              }}
              onTouchEnd={() => {
                void bgMut.mutateAsync({
                  branchId,
                  layoutId: data.layout.id ?? undefined,
                  backgroundImageUrl: bgKey,
                  backgroundOpacity: opacity,
                });
              }}
            />
          </div>
          <PresignedImageUploadTrigger
            keyPrefix={`uploads/warehouse-layouts/${shopId}/${branchId}`}
            label="ອັບໂຫຼດພື້ນຫຼັງ"
            variant="button"
            disabled={bgMut.isPending}
            onSuccess={(key) => {
              setBgKey(key);
              void bgMut.mutateAsync({
                branchId,
                layoutId: data.layout.id ?? undefined,
                backgroundImageUrl: key,
                backgroundOpacity: opacity,
              });
            }}
            onError={() => toast.error("ອັບໂຫຼດລົ້ມເຫຼວ")}
          />
          {bgKey ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBgKey(null);
                void bgMut.mutateAsync({
                  branchId,
                  layoutId: data.layout.id ?? undefined,
                  backgroundImageUrl: null,
                  backgroundOpacity: opacity,
                });
              }}
            >
              ລຶບພື້ນຫຼັງ
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        ref={viewportRef}
        className="relative h-[min(70vh,560px)] touch-none select-none overflow-hidden rounded-lg border border-border bg-[color-mix(in_oklab,var(--muted)_35%,white)]"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          if (editMode && selectedKey) setSelectedKey(null);
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          panDrag.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            originX: pan.x,
            originY: pan.y,
          };
        }}
        onPointerMove={(e) => {
          const nd = nodeDrag.current;
          if (nd && editMode) {
            const dx = (e.clientX - nd.startX) / scale;
            const dy = (e.clientY - nd.startY) / scale;
            if (nd.mode === "move") {
              updateNode(nd.key, {
                x: snap(nd.origX + dx, grid),
                y: snap(nd.origY + dy, grid),
              });
            } else {
              updateNode(nd.key, {
                width: Math.max(grid, snap(nd.origW + dx, grid)),
                height: Math.max(grid, snap(nd.origH + dy, grid)),
              });
            }
            return;
          }
          const d = panDrag.current;
          if (!d || d.pointerId !== e.pointerId) return;
          setPan({
            x: d.originX + (e.clientX - d.startX),
            y: d.originY + (e.clientY - d.startY),
          });
        }}
        onPointerUp={(e) => {
          panDrag.current = null;
          nodeDrag.current = null;
          try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
        }}
        onPointerCancel={() => {
          panDrag.current = null;
          nodeDrag.current = null;
        }}
      >
        <div
          className="absolute origin-top-left will-change-transform"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            width: data.layout.canvasWidth,
            height: data.layout.canvasHeight,
          }}
        >
          <svg
            width={data.layout.canvasWidth}
            height={data.layout.canvasHeight}
            className="block"
            role="img"
            aria-label="ແຜນຜັງສາງ"
          >
            <rect
              x={0}
              y={0}
              width={data.layout.canvasWidth}
              height={data.layout.canvasHeight}
              fill="var(--card)"
              stroke="var(--border)"
            />
            {bgSrc ? (
              <image
                href={bgSrc}
                x={0}
                y={0}
                width={data.layout.canvasWidth}
                height={data.layout.canvasHeight}
                opacity={Math.min(100, Math.max(0, opacity)) / 100}
                preserveAspectRatio="xMidYMid meet"
              />
            ) : null}
            {sortedNodes.map(({ n: node, key }) => {
              const selected =
                selectedKey === key ||
                (Boolean(focusLocationId) &&
                  node.refLocationId === focusLocationId);
              const skuHit =
                hasSkuHighlight &&
                node.kind === "location" &&
                Boolean(node.refLocationId) &&
                highlightLocationIds.has(node.refLocationId!);
              const dimmed =
                hasSkuHighlight &&
                node.kind === "location" &&
                !skuHit;
              const clickable = node.kind === "location";
              const baseFill =
                node.kind === "location"
                  ? locationDensityFill({
                      totalQty: node.occupancy?.totalQty ?? 0,
                      maxQty,
                      isDefaultReceive: Boolean(
                        node.isDefaultReceive ||
                          node.occupancy?.status === "default_receive",
                      ),
                      customColor: node.color,
                    })
                  : node.color || "var(--muted)";
              const fill = dimmed
                ? "color-mix(in oklab, var(--muted) 70%, white)"
                : skuHit
                  ? "color-mix(in oklab, var(--primary) 55%, white)"
                  : baseFill;
              return (
                <g key={key} opacity={dimmed ? 0.35 : 1}>
                  <rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    rx={node.kind === "location" ? 6 : 4}
                    fill={fill}
                    stroke={strokeForNode(node, selected, skuHit, dimmed)}
                    strokeWidth={
                      skuHit || selected ? 3 : node.kind === "zone" ? 1.5 : 2
                    }
                    className={cn(
                      (clickable || editMode) && "cursor-pointer",
                    )}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (!editMode) return;
                      setSelectedKey(key);
                      nodeDrag.current = {
                        key,
                        mode: "move",
                        startX: e.clientX,
                        startY: e.clientY,
                        origX: node.x,
                        origY: node.y,
                        origW: node.width,
                        origH: node.height,
                      };
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editMode) {
                        setSelectedKey(key);
                        return;
                      }
                      if (clickable) onLocationClick?.(node);
                    }}
                  />
                  <text
                    x={node.x + node.width / 2}
                    y={node.y + node.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none fill-foreground"
                    style={{
                      fontSize: node.kind === "zone" ? 14 : 12,
                      fontWeight: node.kind === "zone" || skuHit ? 600 : 500,
                    }}
                  >
                    {node.label}
                  </text>
                  {node.kind === "location" &&
                  (node.occupancy?.totalQty ?? 0) > 0 ? (
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.height / 2 + 14}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="pointer-events-none fill-muted-foreground"
                      style={{ fontSize: 10 }}
                    >
                      qty {node.occupancy?.totalQty}
                    </text>
                  ) : null}
                  {editMode && selected ? (
                    <rect
                      x={node.x + node.width - 10}
                      y={node.y + node.height - 10}
                      width={12}
                      height={12}
                      fill="var(--foreground)"
                      className="cursor-nwse-resize"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        nodeDrag.current = {
                          key,
                          mode: "resize",
                          startX: e.clientX,
                          startY: e.clientY,
                          origX: node.x,
                          origY: node.y,
                          origW: node.width,
                          origH: node.height,
                        };
                      }}
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>

        {nodes.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີຕຳແໝຈ — ສ້າງໃນແທັບລາຍການ ຫຼືອສ້າງຜັງຈາກລາຍການ.
          </div>
        ) : null}
      </div>

      <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <ScanLine className="size-3.5" aria-hidden />
        {editMode
          ? "ລາກເພື່ອຍ້າຍ · ມຸມຂອງເພື່ອຂນາດ · ຖອດອອກຈາກຜັງເທົ່ານັ້ນ"
          : "ລາກເພື່ອປັນ · ສກອລຊູມ · ຄລິກຊ່ອງເພື່ອເປິດສະແກນ"}
      </p>
    </div>
  );
}
